import { DropDownFieldRaw } from '@epages/react-components/build/form/DropDownField'
import { filter } from 'lodash'
import Immutable from 'immutable'
import PropTypes from 'prop-types'
import React from 'react'

import { GotoContext } from '../GotoProvider'
import { generateProductUrl } from '../../urlGenerators'
import compose from '../../utils/compose'
import translate from '../../utils/translate'
import withI18n from '../../components/withI18n'

function stateFromSelection(selection) {
  return Immutable.fromJS(selection || []).reduce(
    (state, entry) => state.set(entry.get('name'), entry.get('value')),
    new Immutable.Map(),
  )
}

class VariationSelection extends React.Component {
  static propTypes = {
    product: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
  }

  static contextType = GotoContext

  get variations() {
    return (this.props.product.variations || {}).items
  }

  constructor(props) {
    super(props)

    // if it is a variation product, prefill the selected attributes with those from the product
    this.attributes = stateFromSelection(props.product.productVariationSelection)
  }

  onChange = (attributeName, value) => {
    const { product } = this.props

    // not using reacts state because it is asynchronous but we need some synchronous state
    this.attributes = this.attributes.set(attributeName, value)
    // only setting state to trigger a rerender
    // eslint-disable-next-line react/no-unused-state
    this.setState({ attributes: this.attributes })

    // check if all attributes are selected
    const allFilled = this.attributes.count() === product.variations.variationAttributes.length

    // check if the selected attributes are already those of the current variation product
    const theSame = this.attributes.equals(stateFromSelection(product.productVariationSelection))

    if (allFilled && !theSame) {
      // find the variation that attribute values are the same as the selected ones
      const result = Immutable.fromJS(this.variations).find((variation) => {
        return variation.get('attributes').equals(this.attributes)
      })

      // navigate to the variation product
      if (result) this.context.goto(generateProductUrl(result.toJS()))
    }
  }

  /**
   * render a dropdown selection of all values for this variation attribute
   *
   * @param {String}      attributeName   the attributes name for this dropdown
   * @param {String}      [value]         the selected value (default empty)
   *
   * @returns {DOMNode}               *
   */
  renderSelection(attributeName, value) {
    // get the values for this variation
    const variationValues = this.props.product.variations.variationAttributes.find(
      (attr) => attr.name === attributeName,
    ).values

    // transform the possible variations of this attribute into an options-list for the dropdown
    const possibleVariations = filter(this.variations, { attributes: this.attributes.delete(attributeName).toJS() })
    const variationKeys = [...new Set(possibleVariations.map((variation) => variation.attributes[attributeName]))]
    const rawVariationOptions = variationKeys.map((variation) => ({
      value: variation,
      label: variationValues.find((val) => val.value === variation)?.displayValue,
    }))

    // sort the variation options by the order of the variation values
    const sortedVariationOptions = variationValues
      .map((value) => rawVariationOptions.find((option) => option.value === value.value))
      // cleanup possible `undefined` values that occur when there is no visible product for a possible variation
      .filter(Boolean)

    // the default option for the dropdown
    const defaultOption = value
      ? []
      : {
          value: '',
          label: this.props.t('components.productComponent.variationSelectionDropdown.label'),
        }
    // combine the default option with the variation options
    const options = [].concat(defaultOption, sortedVariationOptions)

    return (
      <DropDownFieldRaw
        options={options}
        value={value || ''}
        onChange={(value) => this.onChange(attributeName, value)}
        title={this.props.t('components.productComponent.variationSelectionDropdown.explanation')}
      />
    )
  }

  render() {
    if (!this.variations) return null

    const attributes = this.props.product.variations.variationAttributes

    return (
      <div className="product-variation">
        {attributes.map((attribute) => (
          <label className="product-variation-type" key={attribute.name}>
            <span className="product-variation-label">{attribute.displayName}:</span>
            <div className="product-variation-dropdown">
              {this.renderSelection(attribute.name, this.attributes.get(attribute.name))}
            </div>
          </label>
        ))}
      </div>
    )
  }
}

const TranslatedVariationSelection = compose(withI18n('shop'), translate())(VariationSelection)

export default TranslatedVariationSelection
